import { Product, ProductSortableColumn } from "@CommonApi/inputs/products/all";
import { ColDef, ColGroupDef, RowClassParams } from "ag-grid-community";
import { PageStatus } from "src/components";
import { BackendInputFilter } from "src/components/AgGridTable/filters/BackendInputFilter";
import { MultiSelectFilter } from "src/components/AgGridTable/filters/MultiSelectFilter";
import {
  BackendSortHeader,
  BackendSortHeaderParams,
} from "src/components/AgGridTable/headers/BackendSortHeader";
import { Color } from "src/elements";

type SortableHeaderConfig = {
  innerHeaderComponent: typeof BackendSortHeader;
  innerHeaderComponentParams: BackendSortHeaderParams<ProductSortableColumn>;
};

export const getColumns = (
  getSortableHeaderParams: (sortKey: ProductSortableColumn) => SortableHeaderConfig,
  categoryOptions: string[],
  subcategoryOptions: string[],
): (ColDef<Product> | ColGroupDef<Product>)[] => [
  {
    field: "product_id",
    headerName: "Product ID",
  },
  {
    field: "upload_id",
    headerName: "Upload ID",
  },
  {
    field: "upc",
    headerName: "UPC",
    editable: false,
    headerComponentParams: getSortableHeaderParams("upc"),
    filter: BackendInputFilter,
    filterParams: {
      placeholder: "Search UPC",
    },
  },
  {
    field: "product_code",
    headerName: "Product Code",
    editable: false,
    headerComponentParams: getSortableHeaderParams("product_code"),
    filter: BackendInputFilter,
    filterParams: {
      placeholder: "Search product code",
    },
  },
  {
    field: "name",
    headerName: "Name",
    editable: false,
    pinned: "left",
    headerComponentParams: getSortableHeaderParams("name"),
    filter: BackendInputFilter,
    filterParams: {
      placeholder: "Search name",
    },
  },
  {
    field: "size",
    headerName: "Size",
  },
  {
    field: "brand",
    headerName: "Brand",
    editable: false,
    headerComponentParams: getSortableHeaderParams("brand"),
    filter: BackendInputFilter,
    filterParams: {
      placeholder: "Search brand",
    },
  },
  {
    field: "category",
    headerName: "Category",
    headerComponentParams: getSortableHeaderParams("category"),
    filter: MultiSelectFilter,
    filterParams: {
      options: categoryOptions,
    },
  },
  {
    field: "subcategory",
    headerName: "Sub-Category",
    headerComponentParams: getSortableHeaderParams("subcategory"),
    filter: MultiSelectFilter,
    filterParams: {
      options: subcategoryOptions,
    },
  },
  {
    headerName: "Toogle Squeeze",
    children: [
      { headerName: "", columnGroupShow: "closed", filter: false },
      {
        field: "squeeze_width_pct",
        headerName: "Squeeze Width %",
        columnGroupShow: "open",
      },
      {
        field: "squeeze_height_pct",
        headerName: "Squeeze Height %",
        columnGroupShow: "open",
      },
      {
        field: "squeeze_depth_pct",
        headerName: "Squeeze Depth %",
        columnGroupShow: "open",
      },
    ],
  },
  {
    headerName: "Toogle Dimensions",
    children: [
      { headerName: "", columnGroupShow: "closed", filter: false },
      {
        field: "unit_width",
        headerName: "Unit Width",
        columnGroupShow: "open",
      },
      {
        field: "unit_height",
        headerName: "Unit Height",
        columnGroupShow: "open",
      },
      {
        field: "unit_depth",
        headerName: "Unit Depth",
        columnGroupShow: "open",
      },
      {
        field: "tray_width",
        headerName: "Tray Width",
        columnGroupShow: "open",
      },
      {
        field: "tray_height",
        headerName: "Tray Height",
        columnGroupShow: "open",
      },
      {
        field: "tray_depth",
        headerName: "Tray Depth",
        columnGroupShow: "open",
      },
      {
        field: "case_width",
        headerName: "Case Width",
        columnGroupShow: "open",
      },
      {
        field: "case_height",
        headerName: "Case Height",
        columnGroupShow: "open",
      },
      {
        field: "case_depth",
        headerName: "Case Depth",
        columnGroupShow: "open",
      },
      {
        field: "display_width",
        headerName: "Display Width",
        columnGroupShow: "open",
      },
      {
        field: "display_height",
        headerName: "Display Height",
        columnGroupShow: "open",
      },
      {
        field: "display_depth",
        headerName: "Display Depth",
        columnGroupShow: "open",
      },
      {
        field: "tray_number_wide",
        headerName: "Tray Number Wide",
        columnGroupShow: "open",
      },
      {
        field: "tray_number_deep",
        headerName: "Tray Number Deep",
        columnGroupShow: "open",
      },
      {
        field: "tray_number_high",
        headerName: "Tray Number High",
        columnGroupShow: "open",
      },
      {
        field: "tray_total_number",
        headerName: "Tray Total Number",
        columnGroupShow: "open",
      },
      {
        field: "case_number_wide",
        headerName: "Case Number Wide",
        columnGroupShow: "open",
      },
      {
        field: "case_number_deep",
        headerName: "Case Number Deep",
        columnGroupShow: "open",
      },
      {
        field: "case_number_high",
        headerName: "Case Number High",
        columnGroupShow: "open",
      },
      {
        field: "case_total_number",
        headerName: "Case Total Number",
        columnGroupShow: "open",
      },
    ],
  },
  {
    field: "color",
    headerName: "Color",
  },
  {
    field: "sub_commodity",
    headerName: "Sub Commodity",
  },
  {
    field: "internal_comments",
    headerName: "Internal Comments",
  },
  {
    field: "is_disco_item",
    headerName: "Is Disco Item",
  },
  {
    field: "shape_id",
    headerName: "Shape ID",
  },
  {
    field: "manufacturer",
    headerName: "Manufacturer",
  },
];

export const LoadingProductOverlay = () => (
  <PageStatus label="Loading Products..." icon="spinner" />
);

export const EmptyProductOverlay = () => (
  <PageStatus label="Products not found!" icon="alert" color={Color.red} />
);

export const getRowStyle = (params: RowClassParams<Product>) => {
  if (params.rowIndex % 2 === 0) {
    return { backgroundColor: Color.lightGray };
  }
  return { backgroundColor: Color.white };
};
